// File: frontend/src/components/AddEditPersonForm.js
import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Plus } from 'lucide-react';
import { peopleAPI, modelOptionsAPI, casesAPI, businessAPI } from '../utils/api';
import { Building2, X as XIcon } from 'lucide-react';
import { PERSON_CATEGORIES, PERSON_STATUSES, OSINT_DATA_TYPES, CONNECTION_TYPES, LOCATION_TYPES, CRM_STATUSES, updateDynamicConstants } from '../utils/constants';
import { useData } from '../contexts/DataContext';
import { useUI } from '../contexts/UIContext';
import LocationsSection from './person-form/LocationsSection';
import OsintSection from './person-form/OsintSection';
import ConnectionsSection from './person-form/ConnectionsSection';
import CustomFieldsSection from './person-form/CustomFieldsSection';

const EMPTY_FORM = {
  firstName: '', lastName: '', aliases: [], dateOfBirth: '', category: '',
  status: '', crmStatus: '', caseName: '', profilePictureUrl: '', notes: '',
  osintData: [], attachments: [], connections: [], locations: [], custom_fields: {},
  linkedBusinesses: [],
  caseIds: [],
};

const AddEditPersonForm = () => {
  const { people, customFields, fetchPeople, fetchBusinesses, fetchCases } = useData();
  const { editingPerson, setEditingPerson, setShowAddPersonForm } = useUI();
  const person = editingPerson;

  const handleClose = () => { setEditingPerson(null); setShowAddPersonForm(false); };

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [newAlias, setNewAlias] = useState('');
  const [connectionTypes, setConnectionTypes] = useState(CONNECTION_TYPES);
  const [locationTypes, setLocationTypes] = useState(LOCATION_TYPES);
  const [crmStatuses, setCrmStatuses] = useState(CRM_STATUSES);
  const [osintDataTypes, setOsintDataTypes] = useState(OSINT_DATA_TYPES);
  const [existingCases, setExistingCases] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [employerSearch, setEmployerSearch] = useState('');
  const [showEmployerSearch, setShowEmployerSearch] = useState(false);
  const [linkedBizManualMode, setLinkedBizManualMode] = useState(false);
  const [newLinkedBizData, setNewLinkedBizData] = useState({ name: '', type: '', industry: '' });
  const [caseSearch, setCaseSearch] = useState('');
  const [showCaseSearch, setShowCaseSearch] = useState(false);
  const [optionsLoadError, setOptionsLoadError] = useState(false);

  // Employed At state — same pattern as Linked Businesses, stored in linked_businesses with type:'employed'
  const [employedAtList, setEmployedAtList] = useState([]); // [{business_id, role, department, _isNew, _newData, name?}]
  const [showEmployedAtSearch, setShowEmployedAtSearch] = useState(false);
  const [employedAtSearch, setEmployedAtSearch] = useState('');
  const [employedAtManualMode, setEmployedAtManualMode] = useState(false);
  const [newBizData, setNewBizData] = useState({ name: '', type: '', industry: '' });

  useEffect(() => {
    const loadModelOptions = async () => {
      try {
        const options = await modelOptionsAPI.getAll();
        updateDynamicConstants(options);
        const pick = (type) => options
          .filter(o => o.model_type === type && o.is_active)
          .sort((a, b) => a.display_order - b.display_order)
          .map(o => ({ value: o.option_value, label: o.option_label }));
        const conn = pick('connection_type'); if (conn.length) setConnectionTypes(conn);
        const loc  = pick('location_type');  if (loc.length)  setLocationTypes(loc);
        const crm  = pick('crm_status');     if (crm.length)  setCrmStatuses(crm);
        const osint = pick('osint_data_type'); if (osint.length) setOsintDataTypes(osint);
      } catch { setOptionsLoadError(true); }
    };
    const loadCases = async () => {
      try { setExistingCases(await casesAPI.getAll()); }
      catch { setOptionsLoadError(true); }
    };
    const loadBusinesses = async () => {
      try { setBusinesses(await businessAPI.getAll()); }
      catch { console.error('Failed to load businesses'); }
    };
    loadModelOptions();
    loadCases();
    loadBusinesses();
  }, []);

  useEffect(() => {
    if (person) {
      setFormData({
        firstName: person.first_name || '',
        lastName: person.last_name || '',
        aliases: person.aliases || [],
        dateOfBirth: person.date_of_birth ? person.date_of_birth.split('T')[0] : '',
        category: person.category || '',
        status: person.status || '',
        crmStatus: person.crm_status || '',
        caseName: person.case_name || '',
        profilePictureUrl: person.profile_picture_url || '',
        notes: person.notes || '',
        osintData: person.osint_data || [],
        attachments: person.attachments || [],
        connections: person.connections || [],
        locations: person.locations || [],
        custom_fields: person.custom_fields || {},
        // Linked Businesses = linked_businesses without type:'employed'
        linkedBusinesses: (person.linked_businesses || []).filter(b => b.type !== 'employed'),
        caseIds: (person.cases_data || []).map(c => c.id),
      });
      // Employed At = linked_businesses with type:'employed'
      const empAt = (person.linked_businesses || []).filter(b => b.type === 'employed');
      setEmployedAtList(empAt.map(b => ({ business_id: b.business_id, role: b.role || '', department: b.department || '' })));
    }
  }, [person]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Resolve _isNew items in Linked Businesses (same pattern as before)
      const resolvedLinkedBiz = [];
      for (const emp of formData.linkedBusinesses) {
        if (emp._isNew) {
          try {
            const created = await businessAPI.create({ ...emp._newData, employees: [], status: 'active' });
            resolvedLinkedBiz.push({ business_id: created.id, role: emp.role || '', department: emp.department || '', notes: '' });
          } catch (e) { console.error('Failed to create linked business:', e); }
        } else {
          const { _isNew, _newData, name, ...clean } = emp;
          resolvedLinkedBiz.push(clean);
        }
      }

      // Resolve _isNew items in Employed At — exact same pattern
      const resolvedEmployedAt = [];
      for (const emp of employedAtList) {
        if (emp._isNew) {
          try {
            const created = await businessAPI.create({ ...emp._newData, employees: [], status: 'active' });
            resolvedEmployedAt.push({ business_id: created.id, role: emp.role || '', department: emp.department || '', notes: '', type: 'employed' });
          } catch (e) { console.error('Failed to create employed-at business:', e); }
        } else {
          resolvedEmployedAt.push({ business_id: emp.business_id, role: emp.role || '', department: emp.department || '', notes: '', type: 'employed' });
        }
      }

      // Merge both lists into linked_businesses and save with person update
      const allLinkedBusinesses = [...resolvedLinkedBiz, ...resolvedEmployedAt];
      const saveData = { ...formData, linkedBusinesses: allLinkedBusinesses, dateOfBirth: formData.dateOfBirth || null };
      if (person) {
        await peopleAPI.update(person.id, saveData);
      } else {
        await peopleAPI.create(saveData);
      }

      handleClose();
      fetchPeople();
      fetchBusinesses();
      fetchCases();
    } catch (error) {
      console.error('Error saving person:', error);
      alert('Failed to save person: ' + error.message);
    }
  };

  const set = (key, val) => setFormData(f => ({ ...f, [key]: val }));

  const addAlias = () => {
    if (newAlias.trim()) { set('aliases', [...formData.aliases, newAlias.trim()]); setNewAlias(''); }
  };

  const getSimilarCases = () => {
    if (!formData.caseName || formData.caseName.length < 2) return [];
    return existingCases.filter(c => c.case_name.toLowerCase().includes(formData.caseName.toLowerCase())).slice(0, 5);
  };

  const inputClass = 'w-full px-3 py-2 border dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b dark:border-gray-600">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {person ? 'Edit Person' : 'Add New Person'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {optionsLoadError && (
            <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Some options failed to load. Categories and connection types may be incomplete.
            </div>
          )}

          {/* Name */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name *</label>
              <input type="text" value={formData.firstName} onChange={e => set('firstName', e.target.value)} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
              <input type="text" value={formData.lastName} onChange={e => set('lastName', e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* DOB + Category */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date of Birth</label>
              <input type="date" value={formData.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
              <select value={formData.category} onChange={e => set('category', e.target.value)} className={inputClass}>
                <option value="">Select Category</option>
                {PERSON_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Status + CRM Status */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select value={formData.status} onChange={e => set('status', e.target.value)} className={inputClass}>
                <option value="">Select Status</option>
                {PERSON_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">CRM Status</label>
              <select value={formData.crmStatus} onChange={e => set('crmStatus', e.target.value)} className={inputClass}>
                <option value="">Select CRM Status</option>
                {crmStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Cases (multi) + Profile pic */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cases {formData.caseIds.length > 0 && <span className="text-xs text-gray-500 font-normal">({formData.caseIds.length})</span>}
                </label>
                <button type="button" onClick={() => { setShowCaseSearch(s => !s); setCaseSearch(''); }}
                  className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 flex items-center">
                  <Plus className="w-3 h-3 mr-1" />Add
                </button>
              </div>

              {/* Selected cases */}
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.caseIds.map(cid => {
                  const c = existingCases.find(x => x.id === cid);
                  return c ? (
                    <span key={cid} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                      {c.case_name}
                      <button type="button" onClick={() => set('caseIds', formData.caseIds.filter(id => id !== cid))}
                        className="ml-1 hover:text-red-500"><XIcon className="w-3 h-3" /></button>
                    </span>
                  ) : null;
                })}
                {formData.caseIds.length === 0 && !showCaseSearch && (
                  <span className="text-xs text-gray-400 dark:text-slate-500">No cases linked</span>
                )}
              </div>

              {/* Case search */}
              {showCaseSearch && (
                <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                  <input type="text" value={caseSearch} onChange={e => setCaseSearch(e.target.value)}
                    autoFocus placeholder="Search cases…"
                    className="w-full px-3 py-2 text-sm border-b dark:border-gray-600 focus:outline-none dark:bg-slate-700 dark:text-gray-100" />
                  <div className="max-h-36 overflow-y-auto">
                    {existingCases
                      .filter(c => c.case_name.toLowerCase().includes(caseSearch.toLowerCase()))
                      .filter(c => !formData.caseIds.includes(c.id))
                      .map(c => (
                        <button key={c.id} type="button"
                          onClick={() => { set('caseIds', [...formData.caseIds, c.id]); setShowCaseSearch(false); setCaseSearch(''); }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 border-b dark:border-gray-700 last:border-0">
                          {c.case_name}
                        </button>
                      ))}
                    {existingCases.filter(c => !formData.caseIds.includes(c.id) && c.case_name.toLowerCase().includes(caseSearch.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-2">No more cases</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profile Picture URL</label>
              <input type="url" value={formData.profilePictureUrl} onChange={e => set('profilePictureUrl', e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Section 1: Employed At — same pattern as Linked Businesses, stored in linked_businesses with type:'employed' */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Employed At
                {employedAtList.length > 0 && <span className="ml-2 text-xs text-gray-500 font-normal">({employedAtList.length})</span>}
              </label>
              <button type="button"
                onClick={() => { setShowEmployedAtSearch(s => !s); setEmployedAtSearch(''); setEmployedAtManualMode(false); setNewBizData({ name: '', type: '', industry: '' }); }}
                className="px-3 py-1 bg-amber-500 text-white rounded-md hover:bg-amber-600 flex items-center text-sm">
                <Plus className="w-4 h-4 mr-1" />Add Business
              </button>
            </div>

            {showEmployedAtSearch && (
              <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg space-y-2">
                <button type="button"
                  onClick={() => { setEmployedAtManualMode(m => !m); setEmployedAtSearch(''); setNewBizData({ name: '', type: '', industry: '' }); }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  {employedAtManualMode ? '🔍 Select from existing businesses' : '✏️ Enter manually instead (Quick Add)'}
                </button>

                {!employedAtManualMode ? (
                  <>
                    <input type="text" value={employedAtSearch} onChange={e => setEmployedAtSearch(e.target.value)}
                      placeholder="Search business name…" autoFocus className={inputClass} />
                    {employedAtSearch.trim() && (
                      <div className="border dark:border-gray-600 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                        {businesses
                          .filter(b => b.name.toLowerCase().includes(employedAtSearch.toLowerCase()))
                          .filter(b => !employedAtList.some(e => e.business_id === b.id))
                          .map(b => {
                            const isLinked = formData.linkedBusinesses.some(e => e.business_id === b.id);
                            return (
                              <button key={b.id} type="button"
                                onClick={() => {
                                  if (isLinked) set('linkedBusinesses', formData.linkedBusinesses.filter(e => e.business_id !== b.id));
                                  setEmployedAtList(l => [...l, { business_id: b.id, role: '', department: '' }]);
                                  setEmployedAtSearch(''); setShowEmployedAtSearch(false);
                                }}
                                className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-amber-100 dark:hover:bg-amber-900/30 border-b dark:border-gray-700 last:border-0">
                                <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
                                <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{b.name}</span>
                                {b.industry && <span className="text-xs text-gray-500 ml-1">· {b.industry}</span>}
                                {isLinked && <span className="text-xs text-amber-600 ml-1">(ย้ายจาก Linked)</span>}
                              </button>
                            );
                          })}
                        {businesses.filter(b => b.name.toLowerCase().includes(employedAtSearch.toLowerCase()) && !employedAtList.some(e => e.business_id === b.id)).length === 0 && (
                          <p className="text-sm text-gray-400 text-center py-3">No businesses found — try Quick Add</p>
                        )}
                      </div>
                    )}
                    {!employedAtSearch.trim() && <p className="text-xs text-gray-400 dark:text-slate-500">Type to search…</p>}
                  </>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-slate-400">Quick Add Business</p>
                    <input type="text" placeholder="Business Name *" value={newBizData.name}
                      onChange={e => setNewBizData(d => ({ ...d, name: e.target.value }))}
                      className={inputClass} autoFocus />
                    <div className="grid grid-cols-2 gap-2">
                      <select value={newBizData.type} onChange={e => setNewBizData(d => ({ ...d, type: e.target.value }))} className={inputClass}>
                        <option value="">Type (optional)</option>
                        {['Corporation','LLC','Partnership','Sole Proprietorship','Non-Profit','Government','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select value={newBizData.industry} onChange={e => setNewBizData(d => ({ ...d, industry: e.target.value }))} className={inputClass}>
                        <option value="">Industry (optional)</option>
                        {['Technology','Finance','Healthcare','Manufacturing','Retail','Real Estate','Construction','Transportation','Education','Entertainment','Hospitality','Other'].map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button type="button" onClick={() => setShowEmployedAtSearch(false)}
                        className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                      <button type="button"
                        disabled={!newBizData.name.trim()}
                        onClick={() => {
                          if (!newBizData.name.trim()) return;
                          setEmployedAtList(l => [...l, { business_id: null, name: newBizData.name, role: '', department: '', _isNew: true, _newData: { ...newBizData } }]);
                          setNewBizData({ name: '', type: '', industry: '' });
                          setShowEmployedAtSearch(false); setEmployedAtManualMode(false);
                        }}
                        className="px-3 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Employed At list */}
            <div className="space-y-2">
              {employedAtList.length === 0 && !showEmployedAtSearch && (
                <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-2">Not listed as employee in any business</p>
              )}
              {employedAtList.map((emp, i) => {
                const biz = businesses.find(b => b.id === emp.business_id);
                const bizName = emp._isNew ? emp.name : (biz?.name || `Business #${emp.business_id}`);
                return (
                  <div key={i} className="border border-amber-300 dark:border-amber-700 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="font-semibold text-sm text-gray-900 dark:text-slate-100">{bizName}</span>
                        {emp._isNew && <span className="text-xs px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded">New</span>}
                        {!emp._isNew && biz?.industry && <span className="text-xs text-gray-500 dark:text-slate-400">· {biz.industry}</span>}
                        {!emp._isNew && biz?.status && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${biz.status === 'active' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{biz.status}</span>
                        )}
                      </div>
                      <button type="button" onClick={() => setEmployedAtList(l => l.filter((_, idx) => idx !== i))}
                        className="p-1 text-gray-400 hover:text-red-500"><XIcon className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 px-4 py-3 bg-white dark:bg-slate-800">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Role / Position</label>
                        <input type="text" value={emp.role}
                          onChange={e => { const u = [...employedAtList]; u[i] = { ...u[i], role: e.target.value }; setEmployedAtList(u); }}
                          placeholder="e.g. Software Engineer" className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Department</label>
                        <input type="text" value={emp.department}
                          onChange={e => { const u = [...employedAtList]; u[i] = { ...u[i], department: e.target.value }; setEmployedAtList(u); }}
                          placeholder="e.g. Engineering" className={inputClass} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Linked Businesses (editable, mirrors Business → Linked People) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Linked Businesses
                <span className="ml-2 text-xs text-gray-400 dark:text-slate-500 font-normal">(mirrors Business → Linked People)</span>
                {formData.linkedBusinesses.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500 font-normal">({formData.linkedBusinesses.length})</span>
                )}
              </label>
              <button
                type="button"
                onClick={() => { setShowEmployerSearch(s => !s); setEmployerSearch(''); setLinkedBizManualMode(false); setNewLinkedBizData({ name: '', type: '', industry: '' }); }}
                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />Add
              </button>
            </div>

            {showEmployerSearch && (
              <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg space-y-2">
                {/* Toggle mode */}
                <button type="button"
                  onClick={() => { setLinkedBizManualMode(m => !m); setEmployerSearch(''); setNewLinkedBizData({ name: '', type: '', industry: '' }); }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                  {linkedBizManualMode ? '🔍 Select from existing businesses' : '✏️ Enter manually instead (Quick Add)'}
                </button>

                {!linkedBizManualMode ? (
                  /* Search existing */
                  <>
                    <input type="text" value={employerSearch} onChange={e => setEmployerSearch(e.target.value)}
                      placeholder="Search business name…" autoFocus className={inputClass} />
                    {employerSearch.trim() && (
                      <div className="border dark:border-gray-600 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                        {businesses
                          .filter(b => b.name.toLowerCase().includes(employerSearch.toLowerCase()))
                          .filter(b => !formData.linkedBusinesses.some(e => e.business_id === b.id) && !employedAtList.some(e => e.business_id === b.id))
                          .map(b => (
                            <button key={b.id} type="button"
                              onClick={() => {
                                set('linkedBusinesses', [...formData.linkedBusinesses, { business_id: b.id, role: '', department: '', notes: '' }]);
                                setEmployerSearch(''); setShowEmployerSearch(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-green-100 dark:hover:bg-green-900/30 border-b dark:border-gray-700 last:border-0">
                              <Building2 className="w-4 h-4 text-green-500 shrink-0" />
                              <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{b.name}</span>
                              {b.industry && <span className="text-xs text-gray-500 ml-1">· {b.industry}</span>}
                            </button>
                          ))}
                        {businesses.filter(b => b.name.toLowerCase().includes(employerSearch.toLowerCase()) && !formData.linkedBusinesses.some(e => e.business_id === b.id) && !employedAtList.some(e => e.business_id === b.id)).length === 0 && (
                          <p className="text-sm text-gray-400 text-center py-3">No businesses found — try Quick Add</p>
                        )}
                      </div>
                    )}
                    {!employerSearch.trim() && <p className="text-xs text-gray-400 dark:text-slate-500">Type to search…</p>}
                  </>
                ) : (
                  /* Quick Add new business */
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-slate-400">Quick Add Business</p>
                    <input type="text" placeholder="Business Name *" value={newLinkedBizData.name}
                      onChange={e => setNewLinkedBizData(d => ({ ...d, name: e.target.value }))}
                      className={inputClass} autoFocus />
                    <div className="grid grid-cols-2 gap-2">
                      <select value={newLinkedBizData.type} onChange={e => setNewLinkedBizData(d => ({ ...d, type: e.target.value }))} className={inputClass}>
                        <option value="">Type (optional)</option>
                        {['Corporation','LLC','Partnership','Sole Proprietorship','Non-Profit','Government','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select value={newLinkedBizData.industry} onChange={e => setNewLinkedBizData(d => ({ ...d, industry: e.target.value }))} className={inputClass}>
                        <option value="">Industry (optional)</option>
                        {['Technology','Finance','Healthcare','Manufacturing','Retail','Real Estate','Construction','Transportation','Education','Entertainment','Hospitality','Other'].map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button type="button" onClick={() => setShowEmployerSearch(false)}
                        className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                      <button type="button"
                        disabled={!newLinkedBizData.name.trim()}
                        onClick={() => {
                          if (!newLinkedBizData.name.trim()) return;
                          set('linkedBusinesses', [...formData.linkedBusinesses, {
                            business_id: null, role: '', department: '', notes: '',
                            _isNew: true, _newData: { ...newLinkedBizData }, name: newLinkedBizData.name
                          }]);
                          setNewLinkedBizData({ name: '', type: '', industry: '' });
                          setShowEmployerSearch(false); setLinkedBizManualMode(false);
                        }}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {formData.linkedBusinesses.map((emp, i) => {
                const biz = businesses.find(b => b.id === emp.business_id);
                return (
                  <div key={i} className="border border-green-300 dark:border-green-700 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 dark:bg-green-900/20">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                        <span className="font-semibold text-sm text-gray-900 dark:text-slate-100">{emp.name || biz?.name || `Business #${emp.business_id}`}</span>
                        {emp._isNew && <span className="text-xs px-1.5 py-0.5 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">New</span>}
                        {!emp._isNew && biz?.industry && <span className="text-xs text-gray-500 dark:text-slate-400">· {biz.industry}</span>}
                        {!emp._isNew && biz?.status && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${biz.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{biz.status}</span>
                        )}
                      </div>
                      <button type="button" onClick={() => set('linkedBusinesses', formData.linkedBusinesses.filter((_, idx) => idx !== i))}
                        className="p-1 text-gray-400 hover:text-red-500"><XIcon className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 px-4 py-3 bg-white dark:bg-slate-800">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Role / Position</label>
                        <input type="text" value={emp.role}
                          onChange={e => { const u = [...formData.linkedBusinesses]; u[i] = { ...u[i], role: e.target.value }; set('linkedBusinesses', u); }}
                          placeholder="e.g. Consultant" className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Department</label>
                        <input type="text" value={emp.department}
                          onChange={e => { const u = [...formData.linkedBusinesses]; u[i] = { ...u[i], department: e.target.value }; set('linkedBusinesses', u); }}
                          placeholder="e.g. Operations" className={inputClass} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {formData.linkedBusinesses.length === 0 && !showEmployerSearch && (
                <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-2">No linked businesses yet</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes</label>
            <textarea value={formData.notes} onChange={e => set('notes', e.target.value)} className={inputClass} rows="3" />
          </div>

          {/* Aliases */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Aliases</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newAlias}
                onChange={e => setNewAlias(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addAlias())}
                placeholder="Add an alias"
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100 dark:border-gray-600"
              />
              <button type="button" onClick={addAlias} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.aliases.map((alias, i) => (
                <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-slate-700">
                  {alias}
                  <button type="button" onClick={() => set('aliases', formData.aliases.filter((_, idx) => idx !== i))} className="ml-2 text-gray-500 dark:text-slate-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <LocationsSection
            locations={formData.locations}
            locationTypes={locationTypes}
            onChange={val => set('locations', val)}
          />

          <OsintSection
            osintData={formData.osintData}
            osintDataTypes={osintDataTypes}
            onChange={val => set('osintData', val)}
          />

          <ConnectionsSection
            connections={formData.connections}
            connectionTypes={connectionTypes}
            people={people}
            currentPersonId={person?.id}
            onChange={val => set('connections', val)}
          />

          <CustomFieldsSection
            customFields={customFields}
            values={formData.custom_fields}
            onChange={val => set('custom_fields', val)}
          />

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t dark:border-gray-600">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {person ? 'Update' : 'Create'} Person
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditPersonForm;
